// 21hermosa – Shopify Products JSON 모듈 (토큰 불필요)
// 쇼피파이 공개 REST API를 사용합니다. 별도 인증 없이 동작합니다.

const SHOPIFY_STORE = '21hermosa.myshopify.com';

// ── 태그 규칙 (쇼피파이 상품에 적용해야 할 태그) ──────────────────────
// skinquiz:step-1          클렌저
// skinquiz:cleanser-oil    오일 클렌저 (저녁 1차 세안). 이 태그 달린 클렌저만 1차로 노출
//                          ※ 안 달면 폼/2차로 취급. 아침 루틴엔 오일 클렌저 노출 안 됨
// skinquiz:step-2          토너
// skinquiz:step-3          세럼/에센스
// skinquiz:step-4          크림
// skinquiz:step-5          선크림
// skinquiz:am              아침 루틴
// skinquiz:pm              저녁 루틴
// skinquiz:skin-dry        건성
// skinquiz:skin-oily       지성
// skinquiz:skin-combo      복합성
// skinquiz:skin-sensitive  민감성
// skinquiz:concern-dryness   건조/수분
// skinquiz:concern-acne      트러블/여드름
// skinquiz:concern-redness   홍조/진정
// skinquiz:concern-dullness  칙칙함/미백
// skinquiz:concern-poros     모공/피지
// skinquiz:concern-firmeza   탄력/주름
// skinquiz:concern-ojeras    눈가/다크서클
// skinquiz:age-teen        10대
// skinquiz:age-20s         20대
// skinquiz:age-30s         30대
// skinquiz:age-40s         40대 이상
// skinquiz:pregnancy-safe  임산부 안심
// skinquiz:fragrance-free  무향
// skinquiz:sunscreen-physical  무기자차 (물리적 필터)
// skinquiz:sunscreen-chemical  유기자차 (화학적 필터)
// skinquiz:treatment       트리트먼트 크림(필름형/PLA/조이는 크림 등). 달면 메인 보습크림(step-4) 자리에서
//                          제외되고 "보완 트리트먼트"로만 추천됨(보습크림을 대체하지 않음). 안 달면 기존과 동일.
// skinquiz:sunscreen-stick    선크림 텍스처: 스틱/왁스형 (메이크업 위 밀림 위험 → 메이크업 사용자에게 회피)
// skinquiz:sunscreen-cream    선크림 텍스처: 크림형
// skinquiz:sunscreen-serum    선크림 텍스처: 세럼/워터형 (메이크업 위에도 가벼움)
// ────────────────────────────────────────────────────────────────────────

// 전체 상품 캐시 (페이지 로드당 1회만 fetch)
let _allProductsCache = null;
let _fetchPromise = null;

async function _fetchAllProducts() {
  if (_allProductsCache) return _allProductsCache;
  if (_fetchPromise) return _fetchPromise;

  _fetchPromise = (async () => {
    const products = [];
    let page = 1;
    const limit = 250;

    while (true) {
      const url = `https://${SHOPIFY_STORE}/products.json?limit=${limit}&page=${page}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Shopify products.json ${res.status}`);

      const data = await res.json();
      // 공개 products.json은 status 필드를 포함하지 않음 — 이미 published 제품만 반환
      const batch = data.products || [];
      products.push(...batch);

      if (batch.length < limit) break;
      page++;
    }

    _allProductsCache = products;
    return products;
  })();

  return _fetchPromise;
}

function _parseTags(rawTags) {
  if (!rawTags) return [];
  const arr = Array.isArray(rawTags)
    ? rawTags.map(t => t.trim())
    : String(rawTags).split(',').map(t => t.trim()).filter(Boolean);
  // 오타 보정: 's' 누락된 `kinquiz:` → `skinquiz:` (필터 오작동 방지)
  // 오타 보정: 's' 누락된 `age-40` → `age-40s` (예: Dr.Althea 147 Barrier Cream에서 발견)
  return arr
    .map(t => t.replace(/^kinquiz:/, 'skinquiz:'))
    .map(t => t === 'skinquiz:age-40' ? 'skinquiz:age-40s' : t);
}

function _nodeToProduct(p) {
  const tags = _parseTags(p.tags);
  const price = parseFloat(p.variants?.[0]?.price || 0).toFixed(2);
  const imageUrl = p.images?.[0]?.src || '';
  const link = `https://21hermosa.es/products/${p.handle}`;
  const ingTags = tags.filter(t => t.startsWith('skinquiz:ing-')).map(t => t.replace('skinquiz:ing-', ''));

  const desc = p.body_html
    ? p.body_html.replace(/<[^>]+>/g, '').trim()
    : '';

  // Try ingredient engine first; fall back to manual tags if unavailable
  let ingAnalysis = null;
  let iciString = '';
  // displayText: used for ingredient DISPLAY only (not scoring).
  // Priority: structured section → full description text fallback
  let displayText = '';
  if (window.ingredientEngine) {
    iciString = window.ingredientEngine.extractFromHtml(p.body_html || '');
    if (iciString) ingAnalysis = window.ingredientEngine.analyzeIngredients(iciString);
    displayText = iciString ||
      (window.ingredientEngine.extractDisplayText
        ? window.ingredientEngine.extractDisplayText(p.body_html || '')
        : '');
  }
  const _t = tag => tags.includes(tag) ? 3 : 0;

  // hero 성분 표시용 텍스트: 제품명 → 설명 → INCI 순으로 이어붙임.
  // 등장 위치순 정렬이므로 제품명·설명에서 먼저 강조된 hero 성분이 우선 노출됨(함량순 INCI보다 정확).
  const titleStr = p.title || '';
  const heroText = `${titleStr} . ${desc} . ${displayText}`.trim();
  const titleLen = titleStr.length; // 제품명 영역 내 성분은 hero로 최우선

  // 경험 수준(액티브 내성) 보너스용 레티놀/레티날 감지 — 실제 데이터 상 대부분 상품이 body_html에
  // 정식 INCI 리스트가 없어 ingAnalysis.hasRetinoid(전성분 기반)가 못 잡는 경우가 많으므로,
  // 제목·설명 텍스트도 보조로 검사한다. AM 안전장치(isDia)는 오탐 방지를 위해 건드리지 않고 그대로 둔다.
  const _hasRetinoidText = /\bretinol\b|\bretinal\b|retinoic acid|tretinoin|retinaldeh[íi]do|레티놀|레티날/i.test(heroText);

  // keyIngredients: skinquiz:ing-* 태그 우선, 없으면 hero 텍스트에서 추출
  const _ki = (lang) => {
    if (ingTags.length) return ingTags.join(', ');
    if (heroText && window.ingredientEngine?.getHighlightIngredients)
      return window.ingredientEngine.getHighlightIngredients(heroText, lang, 4, titleLen);
    return '';
  };

  // ingredientHighlights: [{name:{es,en,ko}, benefit:{es,en,ko}}] for "why this product" section
  const ingredientHighlights = (heroText && window.ingredientEngine?.getIngredientHighlights)
    ? window.ingredientEngine.getIngredientHighlights(heroText, 3, titleLen)
    : [];

  return {
    id: p.handle,
    variantId: p.variants?.[0]?.id || null,
    brand: p.vendor || '',
    name: p.title,
    price,
    link,
    imageUrl,
    description: { es: desc, en: desc, ko: desc },
    keyIngredients: { es: _ki('es'), en: _ki('en'), ko: _ki('ko') },
    ingredientHighlights,
    ingredientsTags: ingTags,
    pregnancySafe: ingAnalysis ? ingAnalysis.pregnancySafe : tags.includes('skinquiz:pregnancy-safe'),
    fragranceFree:  ingAnalysis ? ingAnalysis.fragranceFree  : tags.includes('skinquiz:fragrance-free'),
    sunscreenType:  tags.includes('skinquiz:sunscreen-physical') ? 'physical'
                  : tags.includes('skinquiz:sunscreen-chemical') ? 'chemical' : 'none',
    // 선크림 텍스처(메이크업 병용 시 밀림 방지용) — 미태그면 null(텍스처 필터에서 중립)
    sunscreenTexture: tags.includes('skinquiz:sunscreen-stick') ? 'stick'
                     : tags.includes('skinquiz:sunscreen-cream') ? 'cream'
                     : tags.includes('skinquiz:sunscreen-serum') ? 'serum' : null,
    // 트리트먼트 크림(필름형/PLA 등) — 메인 보습크림 자리에서 제외되고 보완 트리트먼트로만 추천
    isTreatment: tags.includes('skinquiz:treatment'),
    // 레티놀/레티날 안전장치: 성분에서 감지되면 태그가 am이어도 아침 루틴 노출 안 함.
    // 저농도 데이 제품(예: 브라이트닝 크림 미량 레티날)은 상품에 skinquiz:retinoid-am-ok 태그로 예외 허용.
    isDia:  tags.includes('skinquiz:am') && !(ingAnalysis?.hasRetinoid && !tags.includes('skinquiz:retinoid-am-ok')),
    isNoche: tags.includes('skinquiz:pm'),
    // 경험 수준(액티브 내성) 질문 보정용 — 레티놀/레티날 함유 여부(전성분 감지 + 제목/설명 텍스트 보조 감지)
    hasRetinoid: !!(ingAnalysis?.hasRetinoid) || _hasRetinoidText,
    // 클렌저 종류: 오일(1차) = skinquiz:cleanser-oil 태그. 그 외는 폼/워터(2차)로 취급
    cleanserType: tags.includes('skinquiz:cleanser-oil') ? 'oil'
                : (tags.includes('skinquiz:cleanser-water') || tags.includes('skinquiz:cleanser-foam')) ? 'water'
                : null,
    step: parseInt(tags.find(t => t.startsWith('skinquiz:step-'))?.replace('skinquiz:step-', '') || '0'),
    // 피부타입 태그가 하나라도 있으면 태그 우선 (성분 엔진 자동감지보다 명시적 태그 우선)
    skinTypeScore: (() => {
      const SKIN_T = ['skinquiz:skin-dry','skinquiz:skin-oily','skinquiz:skin-combo','skinquiz:skin-sensitive'];
      const hasSkinTags = SKIN_T.some(t => tags.includes(t));
      if (hasSkinTags) return {
        dry:         _t('skinquiz:skin-dry'),
        oily:        _t('skinquiz:skin-oily'),
        combination: _t('skinquiz:skin-combo'),
        sensitive:   _t('skinquiz:skin-sensitive')
      };
      return ingAnalysis ? ingAnalysis.skinTypeScore : { dry: 0, oily: 0, combination: 0, sensitive: 0 };
    })(),
    concernScore: ingAnalysis ? { ...ingAnalysis.concernScore,
      poros:   _t('skinquiz:concern-poros'),
      firmeza: _t('skinquiz:concern-firmeza'),
      ojeras:  _t('skinquiz:concern-ojeras'),
      // Manchas(색소/기미)는 Luminosidad(칙칙함)와 컬렉션이 분리돼 있지만(collections_taxonomy.md),
      // 성분만으론 구분이 안 돼 태그 전용으로만 판정 — 미태그 제품은 0(dullness로만 매칭, 하위 호환).
      manchas: _t('skinquiz:concern-manchas')
    } : {
      dryness:  _t('skinquiz:concern-dryness'),
      acne:     _t('skinquiz:concern-acne'),
      redness:  _t('skinquiz:concern-redness'),
      dullness: _t('skinquiz:concern-dullness'),
      poros:    _t('skinquiz:concern-poros'),
      firmeza:  _t('skinquiz:concern-firmeza'),
      ojeras:   _t('skinquiz:concern-ojeras'),
      manchas:  _t('skinquiz:concern-manchas')
    },
    // Piel Madura(성숙 피부) — 기존 4개 피부타입과 배타적이지 않은 추가 신호.
    // 예: 건성이면서 성숙 피부인 제품처럼 중복 태깅 가능. skinTypeScore와 별도 필드로 관리.
    maduraScore: _t('skinquiz:skin-madura'),
    ageScore: ingAnalysis ? ingAnalysis.ageScore : {
      teen:     _t('skinquiz:age-teen'),
      twenties: _t('skinquiz:age-20s'),
      thirties: _t('skinquiz:age-30s'),
      forties:  _t('skinquiz:age-40s')
    }
  };
}

// ── 공개 API ──────────────────────────────────────────────────────────────
window.shopifyQuiz = {
  isConfigured() {
    return true; // 토큰 불필요, 항상 활성
  },

  // 결과 화면 진입 시 미리 캐시 워밍 (선택적 최적화)
  prefetch() {
    _fetchAllProducts().catch(() => {});
  },

  async getProductsForStep(stepNum, isAM, skinType, concern, userAge, safetyFilters, sunscreenType, cleanserKind, variant, makeupUsed, experience) {
    let allNodes;
    try {
      allNodes = await _fetchAllProducts();
    } catch (err) {
      console.error('[ShopifyQuiz] fetch error:', err);
      return { best: null, alternatives: [] };
    }

    const stepTag = `skinquiz:step-${stepNum}`;
    const timeTag = isAM ? 'skinquiz:am' : 'skinquiz:pm';

    // 해당 스텝 + 시간대 필터
    let products = allNodes
      .filter(n => {
        const tags = _parseTags(n.tags || '');
        return tags.includes(stepTag) && tags.includes(timeTag);
      })
      .map(_nodeToProduct);

    // 안전 필터
    if (safetyFilters.includes('pregnancy')) products = products.filter(p => p.pregnancySafe);
    if (safetyFilters.includes('fragrance'))  products = products.filter(p => p.fragranceFree);
    // 민감성 피부: 향료 제품 자동 제외 (제외 후 후보가 0이면 원본 유지)
    if (skinType === 'sensitive') {
      const ff = products.filter(p => p.fragranceFree);
      if (ff.length) products = ff;
    }

    // 선크림 제형 필터 (step 5, "any" = 필터 없음)
    if (stepNum === 5 && sunscreenType && sunscreenType !== 'any') {
      products = products.filter(p => p.sunscreenType === sunscreenType);
    }
    // 메이크업 사용자: 스틱/왁스형 선크림 회피(밀림 방지). 제외 후 후보 0이면 원본 유지.
    if (stepNum === 5 && makeupUsed === 'yes') {
      const noStick = products.filter(p => p.sunscreenTexture !== 'stick');
      if (noStick.length) products = noStick;
    }

    // 클렌저 종류 필터 (명시적 태그 기반)
    //  · 1차 오일 세안: skinquiz:cleanser-oil 태그 제품만
    //  · 2차 폼 세안 + 아침 단일 세안: 오일이 아닌 클렌저 (폼/워터/미태그)
    if (stepNum === 1) {
      if (cleanserKind === 'oil') products = products.filter(p => p.cleanserType === 'oil');
      else                        products = products.filter(p => p.cleanserType !== 'oil');
    }

    // 트리트먼트 크림 분리 (step 4): variant='treatment'면 트리트먼트만, 기본은 트리트먼트 제외(진짜 보습크림만)
    if (stepNum === 4) {
      if (variant === 'treatment') products = products.filter(p => p.isTreatment);
      else                         products = products.filter(p => !p.isTreatment);
    }

    if (products.length === 0) return { best: null, alternatives: [] };

    // 점수 계산 (피부타입*3 + 1순위 고민*2 + 2순위 고민*1 + 나이*1)
    // 동점 tiebreaker: 적은 피부타입 태그 수 우선(특이성↑), 동률이면 이름 오름차순
    const SKIN_TAGS = ['skinquiz:skin-dry','skinquiz:skin-oily','skinquiz:skin-combo','skinquiz:skin-sensitive'];
    const tagCounts = new Map(
      allNodes.map(n => [n.handle, _parseTags(n.tags).filter(t => SKIN_TAGS.includes(t)).length])
    );

    const concernArr = Array.isArray(concern) ? concern : (concern ? [concern] : []);
    const c1 = concernArr[0], c2 = concernArr[1];
    // 단계별 가중치 적용 (app.js의 공유 점수 함수 — 단계 성격별 차등 + 나이 강화)
    const _score = (typeof window !== 'undefined' && window.skinquizScore)
      ? window.skinquizScore
      : (p, st, a, b, ag, sn) => (p.skinTypeScore[st]||0)*3 + (a?(p.concernScore[a]||0)*2:0) + (b?(p.concernScore[b]||0)*1:0) + (p.ageScore[ag]||0);
    const scored = products.map(p => ({
      product: p,
      score: _score(p, skinType, c1, c2, userAge, stepNum, experience),
      skinTagCount: tagCounts.get(p.id) ?? 4
    }));
    scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (a.skinTagCount !== b.skinTagCount) return a.skinTagCount - b.skinTagCount; // fewer = more specific
      return a.product.name.localeCompare(b.product.name);
    });

    return {
      best:         scored[0].product,
      alternatives: scored.slice(1, 3).map(x => x.product)
    };
  }
};
